// src/pages/UsersPage.tsx
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useLang } from '../context/LangContext';
import { listUsers, createUser, updateUser, deleteUser, ApiUser } from '../api/users';
import { Button } from '../components/common/Button';
import { Input } from '../components/common/Input';
import { fadeIn } from '../utils/animations';

type UserRow = ApiUser;

/* ── Styled ── */

const PageContainer = styled.div`
  min-height: 100vh;
  padding: ${({ theme }) => `${theme.spacing.xxl} ${theme.spacing.lg}`};
  background-color: ${({ theme }) => theme.colors.background};
`;

const Header = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing.xl};
  animation: ${fadeIn} 0.5s ease-out;
`;

const Title = styled.h1`
  font-size: 2rem;
  color: ${({ theme }) => theme.colors.text};
  font-weight: 700;
`;

const Controls = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: ${({ theme }) => theme.colors.cardBackground};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  overflow: hidden;
  box-shadow: ${({ theme }) => theme.shadows.sm};
  animation: ${fadeIn} 0.5s ease-out;
`;

const Th = styled.th`
  text-align: left;
  padding: ${({ theme }) => `${theme.spacing.sm} ${theme.spacing.md}`};
  font-size: 0.8rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: ${({ theme }) => theme.colors.textLight};
  background: ${({ theme }) => theme.colors.background};
  border-bottom: 2px solid ${({ theme }) => theme.colors.border};
`;

const Td = styled.td`
  padding: ${({ theme }) => `${theme.spacing.sm} ${theme.spacing.md}`};
  font-size: 0.9rem;
  color: ${({ theme }) => theme.colors.text};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  vertical-align: middle;
`;

const Tr = styled.tr`
  &:last-child td { border-bottom: none; }
  &:hover td { background: ${({ theme }) => theme.colors.background}; }
`;

const RoleBadge = styled.span<{ $role: string }>`
  display: inline-block;
  padding: 2px 10px;
  border-radius: 999px;
  font-size: 0.78rem;
  font-weight: 600;
  background: ${({ $role, theme }) =>
    $role === 'admin' ? theme.colors.primary + '22' : theme.colors.border};
  color: ${({ $role, theme }) =>
    $role === 'admin' ? theme.colors.primary : theme.colors.textLight};
`;

const StatusDot = styled.span<{ $active: boolean }>`
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ $active }) => ($active ? '#38a169' : '#e53e3e')};
  margin-right: 6px;
`;

const ActionCell = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.xs};
`;

const IconBtn = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px 6px;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  color: ${({ theme }) => theme.colors.textLight};
  display: flex;
  align-items: center;
  transition: color 0.2s;
  &:hover { color: ${({ theme }) => theme.colors.primary}; }
`;

const DeleteIconBtn = styled(IconBtn)`
  &:hover { color: #e53e3e; }
`;

/* ── Modal ── */

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: ${({ theme }) => theme.spacing.lg};
  animation: ${fadeIn} 0.15s ease-out;
`;

const Modal = styled.div`
  background: ${({ theme }) => theme.colors.cardBackground};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: ${({ theme }) => theme.shadows.lg};
  padding: ${({ theme }) => theme.spacing.xl};
  width: 100%;
  max-width: 380px;
`;

const ModalTitle = styled.h2`
  font-size: 1.2rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const ModalField = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.md};
  label {
    display: block;
    font-size: 0.85rem;
    font-weight: 600;
    color: ${({ theme }) => theme.colors.textLight};
    margin-bottom: ${({ theme }) => theme.spacing.xs};
  }
  select {
    width: 100%;
    padding: ${({ theme }) => theme.spacing.sm};
    border-radius: ${({ theme }) => theme.borderRadius.sm};
    border: 1px solid ${({ theme }) => theme.colors.border};
    background: ${({ theme }) => theme.colors.background};
    color: ${({ theme }) => theme.colors.text};
    font-size: 0.9rem;
  }
`;

const ErrorText = styled.p`
  color: ${({ theme }) => theme.colors.error};
  font-size: 0.85rem;
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const ModalActions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
  margin-top: ${({ theme }) => theme.spacing.lg};
`;

/* ── Toast ── */
const ToastMsg = styled.div`
  position: fixed;
  bottom: 80px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1100;
  padding: 12px 24px;
  border-radius: 8px;
  background: #333;
  color: #fff;
  font-size: 0.9rem;
  box-shadow: 0 4px 16px rgba(0,0,0,0.25);
  animation: fadeInUp 0.3s ease-out;
  @keyframes fadeInUp {
    from { opacity: 0; transform: translateX(-50%) translateY(12px); }
    to { opacity: 1; transform: translateX(-50%) translateY(0); }
  }
`;

/* ── Confirm modal ── */
const ConfirmModal = styled.div`
  background: ${({ theme }) => theme.colors.cardBackground};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing.xl};
  max-width: 420px;
  width: 90%;
  box-shadow: 0 8px 30px rgba(0,0,0,0.15);
`;

const ConfirmActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing.sm};
  margin-top: ${({ theme }) => theme.spacing.lg};
`;

/* ── Component ── */

type ModalMode = 'create' | 'edit' | null;

const UsersPage: React.FC = () => {
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const { t, lang } = useLang();
  const dateLocale = lang === 'ru' ? 'ru-RU' : 'en-US';

  const [users, setUsers] = useState<UserRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [hideGuests, setHideGuests] = useState(true);
  const [modalMode, setModalMode] = useState<ModalMode>(null);
  const [editTarget, setEditTarget] = useState<UserRow | null>(null);
  const [formUsername, setFormUsername] = useState('');
  const [formPassword, setFormPassword] = useState('');
  const [formRole, setFormRole] = useState('user');
  const [formActive, setFormActive] = useState(true);
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);
  const [toastMsg, setToastMsg] = useState('');
  const [confirmState, setConfirmState] = useState<{ message: string; onConfirm: () => void } | null>(null);
  const usernameRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!toastMsg) return;
    const timer = window.setTimeout(() => setToastMsg(''), 3500);
    return () => window.clearTimeout(timer);
  }, [toastMsg]);

  const fetchUsers = async () => {
    try {
      const data = await listUsers();
      setUsers(data);
    } catch {
      setToastMsg(t('usersLoadError'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchUsers(); }, []);

  useEffect(() => {
    if (modalMode) {
      setFormError('');
      setTimeout(() => usernameRef.current?.focus(), 50);
    }
  }, [modalMode]);

  const openCreate = () => {
    setFormUsername('');
    setFormPassword('');
    setFormRole('admin');
    setFormActive(true);
    setEditTarget(null);
    setModalMode('create');
  };

  const openEdit = (u: UserRow) => {
    setFormUsername(u.username);
    setFormPassword('');
    setFormRole(u.role);
    setFormActive(u.is_active);
    setEditTarget(u);
    setModalMode('edit');
  };

  const closeModal = () => { setModalMode(null); setEditTarget(null); };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formUsername.trim()) { setFormError(t('usersErrorUsername')); return; }
    if (!formPassword) { setFormError(t('usersErrorPassword')); return; }
    setSaving(true);
    try {
      await createUser({ username: formUsername.trim(), password: formPassword, role: formRole });
      await fetchUsers();
      closeModal();
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setFormError(detail || t('usersErrorCreate'));
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editTarget) return;
    setSaving(true);
    try {
      await updateUser(editTarget.id, {
        password: formPassword || undefined,
        role: formRole,
        is_active: formActive,
      });
      await fetchUsers();
      closeModal();
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setFormError(detail || t('usersErrorSave'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (u: UserRow) => {
    setConfirmState({
      message: t('usersDeleteConfirm').replace('{name}', u.username),
      onConfirm: async () => {
        try {
          await deleteUser(u.id);
          setUsers((prev) => prev.filter((x) => x.id !== u.id));
        } catch (err: unknown) {
          const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
          setToastMsg(detail || t('usersDeleteError'));
        }
      },
    });
  };

  const displayed = hideGuests ? users.filter((u) => u.role !== 'guest') : users;

  return (
    <PageContainer>
      <Header>
        <Title>{t('usersTitle')}</Title>
        <div style={{ display: 'flex', gap: '12px' }}>
          <Button variant="secondary" size="md" onClick={openCreate}>
            {t('usersBtnCreate')}
          </Button>
          <Button variant="outline" size="md" onClick={() => navigate('/tests')}>
            {t('btnBack')}
          </Button>
        </div>
      </Header>

      <Controls>
        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '0.9rem' }}>
          <input
            type="checkbox"
            checked={hideGuests}
            onChange={(e) => setHideGuests(e.target.checked)}
          />
          {t('usersHideGuests')}
        </label>
        <span style={{ fontSize: '0.85rem', color: '#888' }}>
          {t('usersShown')}: {displayed.length} / {users.length}
        </span>
      </Controls>

      {loading ? (
        <p>{t('genLoading')}</p>
      ) : (
        <Table>
          <thead>
            <tr>
              <Th>ID</Th>
              <Th>{t('usersColUsername')}</Th>
              <Th>{t('usersColRole')}</Th>
              <Th>{t('usersColStatus')}</Th>
              <Th>{t('usersColCreated')}</Th>
              <Th></Th>
            </tr>
          </thead>
          <tbody>
            {displayed.map((u) => (
              <Tr key={u.id}>
                <Td style={{ color: '#888', width: '50px' }}>{u.id}</Td>
                <Td><strong>{u.username}</strong></Td>
                <Td><RoleBadge $role={u.role}>{u.role}</RoleBadge></Td>
                <Td>
                  <StatusDot $active={u.is_active} />
                  {u.is_active ? t('usersStatusActive') : t('usersStatusBlocked')}
                </Td>
                <Td style={{ whiteSpace: 'nowrap' }}>
                  {new Date(u.created_at).toLocaleDateString(dateLocale)}
                </Td>
                <Td>
                  <ActionCell>
                    <IconBtn title={t('usersTooltipEdit')} onClick={() => openEdit(u)}>
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                      </svg>
                    </IconBtn>
                    {u.id !== currentUser?.id && (
                      <DeleteIconBtn title={t('usersTooltipDelete')} onClick={() => handleDelete(u)}>
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="3 6 5 6 21 6"/>
                          <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                          <path d="M10 11v6M14 11v6"/>
                          <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
                        </svg>
                      </DeleteIconBtn>
                    )}
                  </ActionCell>
                </Td>
              </Tr>
            ))}
          </tbody>
        </Table>
      )}

      {modalMode && (
        <Overlay onClick={closeModal}>
          <Modal onClick={(e) => e.stopPropagation()}>
            <ModalTitle>
              {modalMode === 'create' ? t('usersModalCreate') : `${t('usersModalEditPrefix')}: ${editTarget?.username}`}
            </ModalTitle>
            <form onSubmit={modalMode === 'create' ? handleCreate : handleEdit}>
              {modalMode === 'create' && (
                <ModalField>
                  <label>{t('usersLabelUsername')}</label>
                  <Input
                    ref={usernameRef}
                    value={formUsername}
                    onChange={(e) => { setFormUsername(e.target.value); setFormError(''); }}
                    placeholder={t('usersPlaceUsername')}
                    autoComplete="off"
                    fullWidth
                  />
                </ModalField>
              )}
              <ModalField>
                <label>{modalMode === 'create' ? t('usersLabelPassword') : t('usersLabelPasswordEdit')}</label>
                <Input
                  ref={modalMode === 'edit' ? usernameRef : undefined}
                  type="password"
                  value={formPassword}
                  onChange={(e) => { setFormPassword(e.target.value); setFormError(''); }}
                  placeholder={modalMode === 'create' ? t('usersPlacePassword') : '••••••••'}
                  autoComplete="new-password"
                  fullWidth
                />
              </ModalField>
              <ModalField>
                <label>{t('usersLabelRole')}</label>
                <select value={formRole} onChange={(e) => setFormRole(e.target.value)}>
                  <option value="user">user</option>
                  <option value="admin">admin</option>
                </select>
              </ModalField>
              {modalMode === 'edit' && (
                <ModalField>
                  <label>{t('usersLabelStatus')}</label>
                  <select value={formActive ? 'active' : 'inactive'} onChange={(e) => setFormActive(e.target.value === 'active')}>
                    <option value="active">{t('usersStatusActive')}</option>
                    <option value="inactive">{t('usersStatusBlocked')}</option>
                  </select>
                </ModalField>
              )}
              {formError && <ErrorText>{formError}</ErrorText>}
              <ModalActions>
                <Button variant="primary" size="md" type="submit" disabled={saving} fullWidth>
                  {saving ? t('genSaving') : modalMode === 'create' ? t('genCreate') : t('genSave')}
                </Button>
                <Button variant="outline" size="md" type="button" onClick={closeModal} fullWidth>
                  {t('cancelButton')}
                </Button>
              </ModalActions>
            </form>
          </Modal>
        </Overlay>
      )}

      {/* ── Toast ── */}
      {toastMsg && <ToastMsg>{toastMsg}</ToastMsg>}

      {/* ── Confirm modal ── */}
      {confirmState && (
        <Overlay onClick={() => setConfirmState(null)}>
          <ConfirmModal onClick={e => e.stopPropagation()}>
            <p style={{ margin: 0, fontSize: '0.95rem', lineHeight: 1.5 }}>{confirmState.message}</p>
            <ConfirmActions>
              <Button variant="outline" size="sm" onClick={() => setConfirmState(null)}>
                {t('cancelButton')}
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={() => {
                  confirmState.onConfirm();
                  setConfirmState(null);
                }}
              >
                {t('conclusionsConfirmAction')}
              </Button>
            </ConfirmActions>
          </ConfirmModal>
        </Overlay>
      )}
    </PageContainer>
  );
};

export default UsersPage;
